import { Injectable, Injector } from '@angular/core';
import { ScopedTagsService } from '../scoped-tags.service';
import { NoteDeletedOnServerError } from '../../_util/trash.util';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { EnvironmentService } from './environment.service';
import { RemoteChangesService } from './remote-changes.service';
import { SyncStateService } from './sync-state.service';
import { DataRemoteService } from '../data-remote.service';
import { DataOfflineService } from '../data-offline.service';
import { replaceAttachmentIdInHtml } from '../../_util/attachment-ref.util';
import { FileUploadService } from '../file-upload.service';
import { LogService } from '../log.service';
import { AuthService } from '../auth.service';
import { ErrorReportingService } from '../error-reporting.service';
import { Space, SpaceService } from '../space.service';

import { Note } from '../data.service';

type ChangeRow = {
  id?: string;
  changes?: any[];
  deleted?: boolean;
  doc?: Note;
};

/** Per-note push retry state, kept so a persistently-failing note doesn't get
 * retried every single sync cycle. See tasks/synch-improvements.md §2. */
export interface PushFailureState {
  noteId: string;
  failedAttempts: number;
  lastFailedAt: string; // ISO timestamp
  nextRetryAt: string; // ISO timestamp
  lastErrorMessage?: string;
}

/**
 * 🔒 BASELINE SYNC INVARIANT
 *
 * - Baseline sync is DESTRUCTIVE (clears IndexedDB)
 * - Baseline sync MUST NEVER run automatically ON A DATABASE THAT HOLDS DATA
 * - Baseline sync MUST ONLY be triggered via runManualBaseline(), with ONE exception:
 *   runInitialBaselineIfEmpty() (login / startup), which re-verifies inside the cycle lock that the
 *   notes AND attachments stores are both empty, so there is provably nothing to destroy (a fresh
 *   install, a new device, or a first login for this account/database).
 *
 * RULES:
 * - runCycle(), forceSync(), online/visibility handlers MUST NOT call baseline
 * - baselineDone is READ-ONLY state (historical fact), never a trigger
 * - baselineAllowed is the ONLY permission gate and is:
 *     - false by default
 *     - true ONLY inside runManualBaseline() and runInitialBaselineIfEmpty()
 *
 * If this invariant is violated, the app WILL lose local data.
 */
export type SyncReason = 'manual' | 'auto' | 'online' | 'debug' | 'login';

@Injectable({ providedIn: 'root' })
export class SyncCoordinatorService {
  //   private running = false;

  /** Guard: baseline sync must run ONCE */
  private baselineDone = false;
  private baselineAllowed = false;
  private isOnline = false;
  private isVisible = false;

  /**
   * Local-only cursor key used when `SpaceService.getSpaces()` fails — e.g. a backend that hasn't
   * deployed the Spaces endpoints yet (tasks/space_core_client.md is a client-only Stage; the
   * server's `/api/spaces` is a separate, independently-deployed piece). NEVER sent to the server:
   * `toRemoteSpaceId()` below maps it back to `undefined`, i.e. the pre-multi-Space request shape.
   * Losing the ability to list Spaces must not mean losing the ability to sync at all.
   */
  private static readonly LEGACY_SPACE_ID = '__legacy__';

  private toRemoteSpaceId(spaceId: string): string | undefined {
    return spaceId === SyncCoordinatorService.LEGACY_SPACE_ID ? undefined : spaceId;
  }

  // --- Adaptive sync polling backoff -------------------------------------
  // Starts at the floor (30s) and steps up through these levels after each
  // consecutive "idle" periodic cycle (nothing to push, nothing to pull, no
  // error). Resets back to the floor immediately on local edits, the tab/
  // device coming back, a manual sync, or any cycle that actually did work
  // or errored. See tasks/synch-improvements.md §1.
  private readonly MIN_SYNC_INTERVAL_MS = 30_000; // 30 seconds
  private readonly BACKOFF_STEPS_MS = [60_000, 120_000, 300_000, 600_000]; // 1m, 2m, 5m, 10m
  private readonly MAX_SYNC_INTERVAL_MS = 600_000; // 10 minutes
  private currentIntervalMs = this.MIN_SYNC_INTERVAL_MS;
  private idleCycleStreak = 0;
  private periodicTimer: ReturnType<typeof setTimeout> | null = null;
  private lastIncrementalResult: { pushed: number; pulled: number; hadError: boolean } | null = null;

  // --- Per-note push retry backoff ---------------------------------------
  // 1st failure: 30s, 2nd: 1m, 3rd: 2m, 4th: 5m, 5th+: 10m. Cleared on success.
  private readonly PUSH_RETRY_BACKOFF_MS = [30_000, 60_000, 120_000, 300_000, 600_000];
  private readonly pushFailures = new Map<string, PushFailureState>();

  constructor(
    private readonly env: EnvironmentService,
    private readonly remoteChanges: RemoteChangesService,
    private readonly syncState: SyncStateService,
    private readonly remote: DataRemoteService,
    private readonly offline: DataOfflineService,
    private readonly fileUpload: FileUploadService,
    private readonly log: LogService,
    private readonly errorReporter: ErrorReportingService,
    private readonly auth: AuthService,
    private readonly spaces: SpaceService,
    // Optional + last: specs construct this service positionally without it.
    private readonly injector?: Injector,
  ) {
    this.start(); // ✅ automatic, exactly once

    // Local edits should make polling responsive again immediately, even if
    // the interval had backed off while the app was idle.
    this.offline.onNoteDirtied().subscribe(() => {
      this.resetBackoffAndReschedule('note-dirtied');
    });

    // Restore "Last sync" across app restarts/sessions so it doesn't always start
    // at "never" — this is purely a historical display value, not a sync trigger.
    void this.offline.getLastSyncTime().then((restored) => {
      if (restored && (!this.lastSyncCompleted || restored > this.lastSyncCompleted)) {
        this.lastSyncCompleted = restored;
        this.syncCompletedSubject.next(restored);
      }
    });
  }

  private cycleRunning = false;
  private queuedReason: SyncReason | null = null;
  private started = false;
  private lastSyncCompleted: Date | null = null;
  // BehaviorSubject (not Subject) so a late subscriber — e.g. HomeComponent/NoteViewBase
  // ngOnInit running after the async IndexedDB restore below resolves — still immediately
  // receives the last known sync time instead of missing it, which is how "Last sync"
  // used to always read "never" right after an app restart. Seeded null; onSyncCompleted()
  // filters that placeholder out so real subscribers only ever see actual Dates.
  private syncCompletedSubject = new BehaviorSubject<Date | null>(null);

  // Distinct from the browser's own online/offline signal (env.online$): the browser can
  // report "online" (network interface up) while the app's actual server is unreachable
  // (e.g. the VPS is powered off). This reflects whether the last sync attempt that
  // actually talked to the server succeeded, so the UI can tell "offline" apart from
  // "server not responding" and never show a falsely-green status in the latter case.
  // Starts optimistic (true) until the first sync attempt reports otherwise.
  private readonly serverReachableSubject = new BehaviorSubject<boolean>(true);
  readonly serverReachable$ = this.serverReachableSubject.asObservable();

  // True only while a push/pull cycle is actually executing (not merely scheduled/queued),
  // so UI can show a real "Syncing…" state. Its falling edge also marks "cycle ended" for
  // failed cycles, which never emit onSyncCompleted().
  private readonly syncingSubject = new BehaviorSubject<boolean>(false);
  readonly syncing$ = this.syncingSubject.asObservable();

  isServerReachable(): boolean {
    return this.serverReachableSubject.value;
  }

  // SyncCoordinatorService
  //   public init(): void {
  //     if (this.started) return;
  //     this.started = true;
  //     this.start();
  //   }

  private async runPushPullSteps(): Promise<{ pushed: number; pulled: number; hadError: boolean }> {
    this.syncingSubject.next(true);
    try {
      return await this.runPushPullStepsInner();
    } finally {
      this.syncingSubject.next(false);
    }
  }

  private async runPushPullStepsInner(): Promise<{ pushed: number; pulled: number; hadError: boolean }> {
    const attachResult = await this.runPushAttachments();
    const pushResult = await this.runPush();
    await this.flushPendingPrivateTags();
    const pullResult = await this.runPullIncremental();
    const hadError = attachResult.failed > 0 || pushResult.failed > 0 || pullResult.errored;

    // "Last sync" must only ever reflect a fully successful cycle — a cycle with any
    // push/pull/attachment failure (e.g. the server being unreachable) must not update it,
    // otherwise the UI can show a recent "Last sync" time while actually failing to sync.
    if (!hadError) {
      this.emitSyncCompleted();
    }
    this.serverReachableSubject.next(!hadError);

    return {
      pushed: attachResult.pushed + pushResult.pushed,
      pulled: pullResult.applied,
      hadError,
    };
  }

  private async runCycle(
    reason: SyncReason = 'auto',
  ): Promise<{ pushed: number; pulled: number; hadError: boolean }> {
    this.log.append('synch', `Starting sync cycle (reason=${reason})`);

    // ❌ baseline is NEVER allowed here
    if (!this.baselineDone) {
      this.log.append('synch', `Baseline not done — skipping auto sync`);
      return { pushed: 0, pulled: 0, hadError: false };
    }

    return this.runPushPullSteps();
  }

  private async runIncrementalCycle(
    reason: SyncReason = 'auto',
  ): Promise<{ pushed: number; pulled: number; hadError: boolean }> {
    this.log.append('synch', `Starting incremental sync (reason=${reason})`);
    return this.runPushPullSteps();
  }

  // MANUAL ONLY — destructive, explicit
  async forceBaselineOnce(): Promise<boolean> {
    if (this.cycleRunning) {
      this.log.append('synch', '[MANUAL] baseline ignored — sync running');
      return false;
    }

    this.cycleRunning = true;
    try {
      this.log.append('synch', '[MANUAL] baseline triggered');

      // reset in-memory guard only
      this.baselineDone = false;

      await this.runBaselinePull();

      this.baselineDone = true;

      this.log.append('synch', '[MANUAL] baseline completed');
      return true;
    } finally {
      this.cycleRunning = false;
    }
  }

  // MANUAL ONLY — NEVER called automatically
  async runManualBaseline(): Promise<void> {
    if (this.cycleRunning) {
      this.log.append('synch', '[MANUAL] baseline ignored — sync already running');
      return;
    }

    this.cycleRunning = true;
    this.baselineAllowed = true; // 🔓 explicit permission
    this.resetBackoffAndReschedule('manual-baseline');

    try {
      this.log.append('synch', '[MANUAL] baseline triggered');

      // ⚠️ DO NOT touch baselineDone here
      // This function is the ONLY gate, permission is baselineAllowed

      await this.runBaselinePull();

      this.log.append('synch', '[MANUAL] baseline completed');
    } finally {
      this.baselineAllowed = false; // 🔒 lock again immediately
      this.cycleRunning = false;
    }
  }

  /** Call after login/logout switches which account is authenticated (in the
   * same page session — a full page reload picks up the right account for
   * free). Resets ordinary incremental-sync bookkeeping that's cached in
   * memory for the lifetime of this singleton, and re-seeds the pull cursor
   * from the now-current account's persisted state. Does NOT touch
   * baselineAllowed/runBaselinePull() — see the BASELINE SYNC INVARIANT above. */
  onAccountChanged(): void {
    this.pushFailures.clear();
    this.baselineDone = false;
    this.idleCycleStreak = 0;
    this.currentIntervalMs = this.MIN_SYNC_INTERVAL_MS;
    this.lastIncrementalResult = null;
    // Forget in-memory per-Space cursors rather than re-seeding one here: which Spaces exist is
    // async (SpaceService.getSpaces()), and each Space's own pull already re-seeds itself from
    // persisted (now correctly account-scoped) state at the start of its turn — see
    // runPullIncrementalForSpace(). This just prevents a stale cursor from a Space both the old
    // and new account belong to (a SHARED Space) from being visible in between.
    this.remoteChanges.forgetAll();
    this.log.append('synch', '[ACCOUNT] switched — sync state reset');
  }

  // ✅ Call this from anywhere to force a full sync cycle (baseline if needed, else push+pull)
  async forceSync(reason: SyncReason = 'manual'): Promise<boolean> {
    if (reason === 'manual' || reason === 'login') {
      this.resetBackoffAndReschedule('manual-sync');
    }

    if (this.cycleRunning) {
      this.queuedReason = reason;
      return false;
    }

    this.cycleRunning = true;
    try {
      await this.runCycle(reason);
      return true;
    } finally {
      this.cycleRunning = false;

      if (this.queuedReason) {
        const r = this.queuedReason;
        this.queuedReason = null;
        void this.forceSync(r);
      }
    }
  }

  /**
   * Runs a sync right after a successful login (the app would otherwise wait for the next periodic
   * tick). Never blocks the caller. If this account has local data, an incremental sync runs; if the
   * local database is empty (fresh install / new device / first login), the initial download runs
   * instead, since an incremental sync can only update notes that already exist locally.
   */
  async syncAfterLogin(): Promise<void> {
    if (await this.hasNoLocalData()) {
      this.log.append('synch', '[LOGIN] no local data — running initial download');
      await this.runInitialBaselineIfEmpty('login');
      return;
    }
    this.baselineDone = true;
    this.log.append('synch', '[LOGIN] login succeeded — running sync');
    await this.forceIncrementalSync('login');
  }

  /** True when there is nothing local to lose: no notes and no (possibly pending-upload) attachments. */
  private async hasNoLocalData(): Promise<boolean> {
    return !(await this.offline.hasAnyNotes()) && !(await this.offline.hasAnyAttachments());
  }

  /**
   * The ONLY automatic baseline (see the BASELINE SYNC INVARIANT): downloads everything for an
   * authenticated account whose local database is completely empty. Emptiness is re-checked while
   * holding the cycle lock, so a note created in the meantime can never be wiped. Returns whether it ran.
   */
  async runInitialBaselineIfEmpty(trigger: 'login' | 'startup' | 'retry'): Promise<boolean> {
    if (!this.auth.isAuthenticated() || this.cycleRunning) return false;

    this.cycleRunning = true;
    try {
      if (!(await this.hasNoLocalData())) return false;

      this.baselineAllowed = true; // 🔓 only because the local database was just verified empty
      this.resetBackoffAndReschedule('initial-baseline');
      this.log.append('synch', `[BASELINE] initial download on empty local database (trigger=${trigger})`);
      await this.runBaselinePull();
      this.baselineDone = true;
      return true;
    } catch (e: any) {
      // runBaselinePull already logged/reported; nothing local was lost (the database was empty),
      // and the next online/visible event or periodic tick retries.
      this.log.append('synch', `[BASELINE] initial download failed — will retry: ${e?.message ?? String(e)}`);
      return false;
    } finally {
      this.baselineAllowed = false; // 🔒 lock again immediately
      this.cycleRunning = false;
    }
  }

  // ✅ Manual incremental sync only (no baseline)
  async forceIncrementalSync(reason: SyncReason = 'manual'): Promise<boolean> {
    if (reason === 'manual' || reason === 'login') {
      this.resetBackoffAndReschedule('manual-sync');
    }

    if (this.cycleRunning) {
      this.queuedReason = reason;
      return false;
    }

    this.cycleRunning = true;
    try {
      this.lastIncrementalResult = await this.runIncrementalCycle(reason);
      return true;
    } finally {
      this.cycleRunning = false;

      if (this.queuedReason) {
        const r = this.queuedReason;
        this.queuedReason = null;
        void this.forceIncrementalSync(r);
      }
    }
  }

  /** Schedules the next periodic tick `delayMs` from now, replacing any pending one. */
  private schedulePeriodicSync(delayMs: number): void {
    if (this.periodicTimer) {
      clearTimeout(this.periodicTimer);
    }
    this.periodicTimer = setTimeout(() => {
      void this.runPeriodicSyncTick();
    }, delayMs);
  }

  /** Resets the adaptive interval to the floor and reschedules. Called on every
   * explicit "be responsive again" trigger (local edit, tab/device back, manual sync). */
  private resetBackoffAndReschedule(reason: string): void {
    const wasNotAtFloor = this.currentIntervalMs !== this.MIN_SYNC_INTERVAL_MS;
    this.currentIntervalMs = this.MIN_SYNC_INTERVAL_MS;
    this.idleCycleStreak = 0;
    if (wasNotAtFloor) {
      this.log.append('synch', `[BACKOFF] reset to ${this.MIN_SYNC_INTERVAL_MS}ms (${reason})`);
    }
    this.schedulePeriodicSync(this.MIN_SYNC_INTERVAL_MS);
  }

  /** Steps the adaptive interval up to the next backoff level after an idle cycle. */
  private advanceBackoffAfterIdleCycle(): void {
    const stepIndex = Math.min(this.idleCycleStreak, this.BACKOFF_STEPS_MS.length - 1);
    const next = Math.min(this.BACKOFF_STEPS_MS[stepIndex], this.MAX_SYNC_INTERVAL_MS);
    this.idleCycleStreak++;
    if (next !== this.currentIntervalMs) {
      this.log.append('synch', `[BACKOFF] idle — increasing sync interval to ${next}ms`);
    }
    this.currentIntervalMs = next;
  }

  private async runPeriodicSyncTick(): Promise<void> {
    if (this.cycleRunning) {
      this.schedulePeriodicSync(this.currentIntervalMs);
      return;
    }
    if (!this.isOnline || !this.isVisible) {
      this.schedulePeriodicSync(this.currentIntervalMs);
      return;
    }

    const hasNotes = await this.offline.hasAnyNotes();
    if (!hasNotes) {
      // Empty local database: retry the initial download (e.g. an earlier attempt failed offline).
      // Skipped once one succeeded, so an account with no notes on the server doesn't re-download every tick.
      if (!this.baselineDone) {
        await this.runInitialBaselineIfEmpty('retry');
      }
      this.schedulePeriodicSync(this.currentIntervalMs);
      return;
    }

    const ran = await this.forceIncrementalSync('auto');
    if (!ran) {
      // Another trigger already had a cycle running; our request was queued onto it.
      // Leave backoff state untouched and just reschedule at the current interval.
      this.schedulePeriodicSync(this.currentIntervalMs);
      return;
    }

    const result = this.lastIncrementalResult;
    const didWork = !!result && (result.pushed > 0 || result.pulled > 0);
    const hadError = !!result?.hadError;

    if (didWork || hadError) {
      // "A failed sync must not be treated as idle" — neither did-work nor errored
      // cycles count toward the idle streak, and both bring polling back to the floor.
      this.currentIntervalMs = this.MIN_SYNC_INTERVAL_MS;
      this.idleCycleStreak = 0;
      this.log.append(
        'synch',
        `[BACKOFF] cycle ${hadError ? 'had errors' : 'did work'} — interval reset to ${this.MIN_SYNC_INTERVAL_MS}ms`,
      );
    } else {
      this.advanceBackoffAfterIdleCycle();
    }

    this.schedulePeriodicSync(this.currentIntervalMs);
  }

  // private startPeriodicIncrementalSync(): void {

  // setInterval(() => {
  //     // hard safety checks
  // console.log('Periodic incremental sync check:', {
  //   baselineDone: this.baselineDone,
  //   cycleRunning: this.cycleRunning,
  //   isOnline: this.isOnline,
  //   isVisible: this.isVisible,
  // });
  //     const hasLocalData = await this.offline.hasAnyNotes();
  //     //if (!this.baselineDone) return;
  //     if (!hasLocalData) return;

  //     if (this.cycleRunning) return;
  //     if (!this.isOnline || !this.isVisible) return;

  //     this.log.append('synch', '[TIMER] periodic incremental sync');
  //     void this.forceIncrementalSync('auto');
  //   }, this.AUTO_SYNC_INTERVAL_MS);
  // }

  // ------------------------------------------------------------------
  // ENTRY POINT
  // ------------------------------------------------------------------
  private start(): void {
  //  this.log.append('synch', '[INIT] SyncCoordinator started');

    queueMicrotask(() => {
      combineLatest([this.env.online$, this.env.visibility$]).subscribe(([online, visibility]) => {
        // ✅ store latest state
        this.isOnline = online === true;
        this.isVisible = visibility === 'visible';

        //this.log.append('synch', `[STATE] online=${this.isOnline}, visibility=${this.isVisible}`);

        if (!this.isOnline || !this.isVisible) return;

        // The tab regaining focus or the device coming back online should make
        // polling responsive again immediately, even if it had backed off.
        this.resetBackoffAndReschedule('online-or-visible');

        void (async () => {
          if (!this.baselineDone) {
            const hasBaseline = await this.offline.hasAnyNotes();
            if (hasBaseline) {
              this.baselineDone = true;
            } else {
              // Nothing local at all -> safe to download automatically (no-op unless logged in).
              this.log.append('synch', '[STATE] no local data — attempting initial download');
              void this.runInitialBaselineIfEmpty('startup');
              return;
            }
          }
          void this.forceSync('online');
        })();
      });
    });

    // 🔁 periodic incremental sync (adaptive interval)
    this.schedulePeriodicSync(this.currentIntervalMs);
  }

  private OLD__start(): void {
    this.log.append('synch', '[INIT] SyncCoordinator started');

    queueMicrotask(() => {
      combineLatest([this.env.online$, this.env.visibility$]).subscribe(([online, visibility]) => {
        //this.log.append('synch', `[STATE] online=${online}, visibility=${visibility}`);

        if (!online || visibility !== 'visible') return;

        void (async () => {
          const hasBaseline = await this.offline.hasBaselineData();

          if (hasBaseline) {
            this.baselineDone = true;
            await this.forceIncrementalSync('online');
          } else {
            this.log.append('synch', '[STATE] no baseline data present — waiting for manual baseline');
          }
        })();
      });
    });
  }

  // ------------------------------------------------------------------
  // BASELINE PULL (AUTHORITATIVE)
  // ------------------------------------------------------------------
  private async runBaselinePull(): Promise<void> {
    // 🔒 SAFETY GUARD:
    // Baseline must NEVER run unless explicitly allowed by the manual baseline entry point.
    // This permanently prevents "baseline on startup" bugs.
    if (this.baselineAllowed !== true) {
      const msg = '[BASELINE] blocked — baselineAllowed is false (manual-only)';
      this.log.append('synch', msg);
      throw new Error(msg);
    }

    this.log.append('synch', '[BASELINE] starting authoritative pull');

    try {
      // 1️⃣ Every Space the user belongs to is a separate database server-side (see
      // tasks/space_core_client.md, Stage 2 §4) — baseline must download all of them, not just
      // the user's own PRIVATE one, or notes in a SHARED Space would silently never appear.
      let spaces: Space[];
      try {
        spaces = await firstValueFrom(this.spaces.getSpaces());
        this.log.append('synch', `[BASELINE] fetching ${spaces.length} space(s)`);
      } catch (e: any) {
        // Listed BEFORE clearing anything: if this account already syncs per Space, a legacy
        // baseline would download only the PRIVATE Space while the SHARED Spaces' cursors stay put,
        // so their notes would never come back — abort with local data untouched instead.
        if (this.syncState.hasSpaceCursors(SyncCoordinatorService.LEGACY_SPACE_ID)) {
          throw new Error(`could not list spaces (${e?.message ?? String(e)}) — baseline aborted, local data untouched`);
        }
        // Fall back to the pre-multi-Space single-database baseline on a backend without the
        // Spaces endpoint.
        this.log.append(
          'synch',
          `[BASELINE] could not list spaces (${e?.message ?? String(e)}) — falling back to legacy single-Space baseline`,
        );
        spaces = [
          {
            id: SyncCoordinatorService.LEGACY_SPACE_ID,
            name: 'Legacy',
            type: 'PRIVATE',
          } as Space,
        ];
      }

      // 2️⃣ Clear local stores
      await this.offline.clearIndexedDbStores();
      this.log.append('synch', '[BASELINE] local IndexedDB cleared');

      // 3️⃣ Fetch everything from every Space, merging into one list. A note's _id is already
      // globally unique across Spaces (UUID-based), so no cross-Space collision is expected.
      let notes: Note[] = [];
      for (const space of spaces) {
        const spaceNotes = await firstValueFrom(this.remote.getAllNotes(this.toRemoteSpaceId(space.id)));
        this.log.append('synch', `[BASELINE] fetched ${spaceNotes.length} notes from "${space.name}" (${space.id})`);
        notes = notes.concat(spaceNotes);
      }

      // 4️⃣ Write to IndexedDB (authoritative)
      await this.offline.syncNotesFromCouchDB(notes);
      await this.syncAttachmentsForNotes(notes, '[BASELINE]');

      // 5️⃣ Determine each Space's authoritative update_seq and acknowledge its pull, so the next
      // incremental sync for that Space resumes from here instead of re-downloading everything.
      for (const space of spaces) {
        const changes = await this.remote.getCouchDBChanges('0', this.toRemoteSpaceId(space.id));
        this.remoteChanges.markPulled(space.id, changes.last_seq);
      }

      // 6️⃣ Notify DataService to reload from IndexedDB
      this.offline.emitBaselineCompleted();

      this.log.append(
        'synch',
        `[BASELINE] completed (${spaces.length} space(s), ${notes.length} notes total)`,
      );
      this.emitSyncCompleted();
      this.serverReachableSubject.next(true);
    } catch (e: any) {
      this.log.append('synch', `[BASELINE] failed: ${e?.message ?? String(e)}`);
      this.errorReporter.report({
        message: `Baseline sync failed: ${e?.message ?? String(e)}`,
        error_type: 'SyncBaselineFailure',
        stack_trace: e?.stack,
      });
      this.serverReachableSubject.next(false);
      throw e;
    }
  }

  onSyncCompleted(): Observable<Date> {
    return this.syncCompletedSubject.asObservable().pipe(filter((time): time is Date => time !== null));
  }

  getLastSyncCompleted(): Date | null {
    return this.lastSyncCompleted;
  }

  private emitSyncCompleted(time: Date = new Date()): void {
    this.lastSyncCompleted = time;
    this.syncCompletedSubject.next(time);
    // Persist so "Last sync" survives app restarts/sessions instead of showing "never".
    void this.offline.setLastSyncTime(time).catch((err) =>
      this.log.append('synch', `Failed to persist last sync time: ${err?.message ?? String(err)}`),
    );
  }
  // ------------------------------------------------------------------
  // INCREMENTAL PULL (id-only changes → fetch docs)
  // ------------------------------------------------------------------

  /**
   * Every Space the user belongs to is a separate database server-side (tasks/space_core_client.md,
   * Stage 2 §4) — pulls one at a time, merging applied/errored across all of them. One Space
   * erroring (e.g. a transient fetch failure) does not stop the others from being attempted; a
   * failure to even enumerate Spaces at all is reported the same way a single-Space pull failure
   * always has been (errored:true, next cycle retries).
   */
  private async runPullIncremental(): Promise<{ applied: number; errored: boolean }> {
    let spaces: Space[];
    try {
      spaces = await firstValueFrom(this.spaces.getSpaces());
    } catch (e: any) {
      // Once this account has synced per Space, the backend is known to support Spaces, so this is a
      // transient failure: skip the pull and retry next cycle. The legacy pull must NOT run then —
      // it replays the PRIVATE feed without a spaceId, so the moved-note delete guard in
      // runPullIncrementalForSpace() can't apply and notes moved into a SHARED Space get deleted
      // locally, while that Space's cursor (already past them) never brings them back.
      if (this.syncState.hasSpaceCursors(SyncCoordinatorService.LEGACY_SPACE_ID)) {
        this.log.append('synch', `[PULL] could not list spaces (${e?.message ?? String(e)}) — skipping pull this cycle`);
        return { applied: 0, errored: true };
      }
      // Losing the ability to list Spaces (e.g. a backend that hasn't deployed the Spaces endpoints
      // yet) must not mean losing the ability to sync at all: fall back to the pre-multi-Space
      // single-database pull (no spaceId filter, legacy cursor) instead of erroring out every note.
      this.log.append(
        'synch',
        `[PULL] could not list spaces (${e?.message ?? String(e)}) — falling back to legacy single-Space pull`,
      );
      return this.runPullIncrementalForSpace(SyncCoordinatorService.LEGACY_SPACE_ID, true);
    }

    let totalApplied = 0;
    let anyErrored = false;

    for (const space of spaces) {
      const { applied, errored } = await this.runPullIncrementalForSpace(space.id, space.type === 'PRIVATE');
      totalApplied += applied;
      if (errored) anyErrored = true;
    }

    return { applied: totalApplied, errored: anyErrored };
  }

  private async runPullIncrementalForSpace(
    spaceId: string,
    isPrivateSpace: boolean,
  ): Promise<{ applied: number; errored: boolean }> {
    // `spaceId` is the local cursor key; `remoteSpaceId` is what actually goes on the wire. They
    // differ only for the getSpaces()-failure fallback (SyncCoordinatorService.LEGACY_SPACE_ID
    // above), where remoteSpaceId is `undefined` — the pre-multi-Space request shape.
    const remoteSpaceId = this.toRemoteSpaceId(spaceId);

    // Always re-seed from persisted state (cheap — localStorage) rather than trusting whatever the
    // in-memory subject already holds: correct even right after an account switch, or the first
    // time this Space is ever seen (a freshly-joined SHARED Space) — see
    // RemoteChangesService.loadPersistedSeq()/forgetAll().
    this.remoteChanges.loadPersistedSeq(spaceId, isPrivateSpace);
    let since = this.remoteChanges.getDebugState(spaceId).lastPulledSeq || '0';
    let updateSeqStr: string | undefined;

    try {
      const info = await firstValueFrom(this.remote.getNotesInfo(remoteSpaceId));
      const updateSeq = info?.status === 'success' ? info.data?.update_seq : undefined;
      updateSeqStr = updateSeq !== undefined && updateSeq !== null ? String(updateSeq) : undefined;
      const lastPulledNum = Number(since);
      const updateSeqNum = Number(updateSeq);
      if (Number.isFinite(lastPulledNum) && Number.isFinite(updateSeqNum) && lastPulledNum > updateSeqNum) {
        this.log.append(
          'synch',
          `[space:${spaceId}] Last_seq > server update_seq; resetting cursor`,
        );
        this.remoteChanges.resetLastSeq(spaceId);
        since = '0';
      }
    } catch (e: any) {
      this.log.append('synch', `[space:${spaceId}] Notes info check failed: ${e?.message ?? String(e)}`);
    }

    // this.log.append(
    //   'synch',
    // //   `[DECISION] detect remote changes (since=${since}, update_seq=${updateSeqStr ?? 'unknown'})`,
    // );

    const maxPages = 20;
    let page = 0;
    let totalApplied = 0;
    let totalSkippedLocalDirty = 0;
    let totalIgnoredMissing = 0;
    let lastSeq = since;

    while (page < maxPages) {
      let res;
      try {
        res = await this.remoteChanges.checkOnce(spaceId, since, remoteSpaceId);
      } catch (e: any) {
        this.log.append('synch', `[space:${spaceId}] [DECISION] changes fetch failed: ${e?.message ?? String(e)}`);
        this.errorReporter.report({
          message: `Incremental pull failed for space ${spaceId}: ${e?.message ?? String(e)}`,
          error_type: 'SyncPullFailure',
          stack_trace: e?.stack,
        });
        return { applied: totalApplied, errored: true };
      }

      if (!res || !Array.isArray(res.results) || res.results.length === 0) {
        const emptySeq = res?.last_seq ?? since;
        this.remoteChanges.markPulled(spaceId, emptySeq);
        this.log.append(
          'synch',
          `[space:${spaceId}] No pending work`,
        );
        if (totalApplied > 0) {
          this.offline.emitIncrementalChangesApplied();
        }
        return { applied: totalApplied, errored: false };
      }

      const filteredResults = (res.results as ChangeRow[]).filter((row) => {
        const id = String(row?.id ?? '');
        return id.startsWith('note:') || id.startsWith('template:');
      });

      this.log.append(
        'synch',
        `Processing ${filteredResults.length} change rows`,
      );

      if (filteredResults.length === 0) {
        lastSeq = res.last_seq;
        since = lastSeq;
        page++;
        if (updateSeqStr && lastSeq !== updateSeqStr) continue;
        break;
      }

      // Apply each change:
      // - if deleted => remove locally (unless local has unsynced changes)
      // - else => fetch doc and merge into local (unless local has unsynced changes)
      let applied = 0;
      let skippedLocalDirty = 0;
      let ignoredMissing = 0;
      let fetchFailed = 0;
      const fetchFailedIds: string[] = [];
      const notesNeedingAttachmentSync = new Map<string, Note>();

      for (const row of filteredResults) {
        const id = String(row?.id ?? '');
        if (!id) continue;

        const local = await this.offline.getNote(id);

        // SERVER DELETE (incl. a move to Trash, which the feed reports as a delete)
        if (row?.deleted === true) {
          const outcome = await this.applyServerDelete(id, local, remoteSpaceId);
          if (outcome === 'applied') applied++;
          else if (outcome === 'skipped-dirty') skippedLocalDirty++;
          else ignoredMissing++;
          continue;
        }

        // SERVER UPSERT: fetch the full doc
        let remoteDoc = row?.doc ?? null;

        if (!remoteDoc || !remoteDoc._id) {
          // The bulk fetch (by the exact rev the feed listed) returned nothing for this note — e.g.
          // that rev is no longer available (superseded + compacted), or the note became unavailable
          // (moved to Trash / purged / moved away) after the feed was read. Never let one such row
          // block this Space's pull forever: ask for the note's CURRENT version by id instead.
          const fallback = await this.fetchCurrentNote(id, remoteSpaceId);
          if (fallback.kind === 'unavailable') {
            this.log.append('synch', `...${id} is no longer available on the server — treating as deleted`);
            const outcome = await this.applyServerDelete(id, local, remoteSpaceId);
            if (outcome === 'applied') applied++;
            else if (outcome === 'skipped-dirty') skippedLocalDirty++;
            else ignoredMissing++;
            continue;
          }
          if (fallback.kind === 'error') {
            this.log.append('synch', `...Pull fetch returned empty doc for ${id} (retry by id failed: ${fallback.message})`);
            fetchFailed++;
            fetchFailedIds.push(id);
            continue;
          }
          this.log.append('synch', `...${id}: bulk fetch empty, fetched current version by id`);
          remoteDoc = fallback.note;
        }

        // Treat Status=Deleted from server as delete as well
        if ((remoteDoc as any).deleted === true || remoteDoc.Status === 'Deleted') {
          if (!local) {
            this.log.append('synch', `...Ignoring delete-status for ${id}: not present locally`);
            ignoredMissing++;
            continue;
          }
          if (local.syncStatus && local.syncStatus !== 'synced') {
            this.log.append('synch', `...Skipping delete-status ${id}: local changes present`);
            skippedLocalDirty++;
            continue;
          }
          await this.offline.deleteNote(id);
          await this.offline.deleteAttachmentsByNote(id);
          applied++;
          continue;
        }

        // If local missing: just store server copy
        if (!local) {
          this.log.append('synch', `...Adding new note: "${remoteDoc.Title}" (${id}) from server`);
          await this.offline.saveNote(remoteDoc, true /*syncMode*/);
          notesNeedingAttachmentSync.set(id, remoteDoc);
          applied++;
          continue;
        }

        // If local has unsynced work, do NOT overwrite
        if (local.syncStatus && local.syncStatus !== 'synced') {
          this.log.append('synch', `...!! Skipping note "${remoteDoc.Title}" (${id}) : local changes present`);
          skippedLocalDirty++;
          continue;
        }

        // Local is clean: only apply if rev changed
        if (local._rev && remoteDoc._rev && local._rev === remoteDoc._rev) {
          // already same
          continue;
        }

        this.log.append('synch', `...Updating existing note: "${remoteDoc.Title}": (${id}) from server`);
        await this.offline.saveNote({ ...local, ...remoteDoc }, true /*syncMode*/);
        notesNeedingAttachmentSync.set(id, remoteDoc);
        applied++;
      }

      if (notesNeedingAttachmentSync.size > 0) {
        await this.syncAttachmentsForNotes(Array.from(notesNeedingAttachmentSync.values()), '[PULL]');
      }

      if (fetchFailed > 0) {
        this.log.append('synch', `[space:${spaceId}] [PULL] not acknowledging last_seq due to fetch errors: failed=${fetchFailed}`);
        this.log.append(
          'synch',
          `[space:${spaceId}] [PULL] failed ids: ${fetchFailedIds.slice(0, 10).join(', ')}${fetchFailedIds.length > 10 ? ' ...' : ''}`,
        );
        const appliedSoFar = totalApplied + applied;
        if (appliedSoFar > 0) {
          this.offline.emitIncrementalChangesApplied();
        }
        return { applied: appliedSoFar, errored: true };
      }

      totalApplied += applied;
      totalSkippedLocalDirty += skippedLocalDirty;
      totalIgnoredMissing += ignoredMissing;

      lastSeq = res.last_seq;
      since = lastSeq;
      page++;

      if (updateSeqStr && lastSeq === updateSeqStr) {
        break;
      }
    }

    if (page >= maxPages && updateSeqStr && lastSeq !== updateSeqStr) {
      this.log.append(
        'synch',
        `[space:${spaceId}] [PULL] stopped after ${maxPages} pages (last_seq=${lastSeq}, update_seq=${updateSeqStr})`,
      );
    }

    this.remoteChanges.markPulled(spaceId, lastSeq);
    this.log.append(
      'synch',
      `[space:${spaceId}] [PULL] completed: applied=${totalApplied}, skippedLocalDirty=${totalSkippedLocalDirty}, ignoredMissing=${totalIgnoredMissing} (last_seq=${lastSeq}, pages=${page})`,
    );

    if (totalApplied > 0) {
      this.offline.emitIncrementalChangesApplied();
    }

    return { applied: totalApplied, errored: false };
  }

  // ------------------------------------------------------------------
  // PUSH (local dirty → server)
  // ------------------------------------------------------------------

  /** Returns the note's PushFailureState, if any. Exposed for `/sync-list` and future UI. */
  getPushFailureState(noteId: string): PushFailureState | undefined {
    return this.pushFailures.get(noteId);
  }

  /** All notes currently in push-retry backoff. Exposed for `/sync-list` and future UI. */
  getAllPushFailureStates(): PushFailureState[] {
    return Array.from(this.pushFailures.values());
  }

  private recordPushFailure(noteId: string, message: string): void {
    const previous = this.pushFailures.get(noteId);
    const failedAttempts = (previous?.failedAttempts ?? 0) + 1;
    const stepIndex = Math.min(failedAttempts - 1, this.PUSH_RETRY_BACKOFF_MS.length - 1);
    const delayMs = this.PUSH_RETRY_BACKOFF_MS[stepIndex];
    const now = Date.now();

    this.pushFailures.set(noteId, {
      noteId,
      failedAttempts,
      lastFailedAt: new Date(now).toISOString(),
      nextRetryAt: new Date(now + delayMs).toISOString(),
      lastErrorMessage: message,
    });
  }

  private async runPush(): Promise<{ pushed: number; failed: number }> {
    // Identify dirty notes in IndexedDB
    const all = await this.offline.getAllNotes();
    // Local drafts (new notes still being edited, see Note.localDraft) wait for their first explicit save.
    const dirty = (all ?? []).filter((n: any) => n?.syncStatus && n.syncStatus !== 'synced' && !n.localDraft);

    if (dirty.length === 0) {
      //this.log.append('synch', '[PUSH] none');
      return { pushed: 0, failed: 0 };
    }

    // Skip notes that failed recently and are still in their retry backoff window.
    const now = Date.now();
    const readyToPush = dirty.filter((note: any) => {
      const failure = this.pushFailures.get(String(note?._id ?? ''));
      return !failure || now >= Date.parse(failure.nextRetryAt);
    });
    const deferredCount = dirty.length - readyToPush.length;

    if (readyToPush.length === 0) {
      this.log.append('synch', `[PUSH] all ${dirty.length} dirty note(s) deferred by retry backoff`);
      return { pushed: 0, failed: 0 };
    }

    this.log.append(
      'synch',
      `[PUSH] ${readyToPush.length} dirty note(s)` +
        (deferredCount > 0 ? ` (${deferredCount} deferred by retry backoff)` : ''),
    );

    let pushed = 0;
    let failed = 0;
    let warned = 0;

    for (const note of readyToPush) {
      const id = note?._id ?? '(missing _id)';

      try {
        // Local delete (a move to Trash made on this device, possibly offline): send it as the
        // server's normal DELETE, which now soft-deletes into Trash (tasks/delete_trash_client.md).
        if (note.syncStatus === 'deleted' || note.Status === 'Deleted') {
          try {
            await firstValueFrom(this.remote.deleteNote(note._id, note._rev ?? '', note.spaceId));
          } catch (deleteErr: any) {
            // 404: already trashed/purged on the server (e.g. from another device) — same end state.
            if (Number(deleteErr?.status) !== 404) throw deleteErr;
            this.log.append('synch', `[PUSH] ${id} already deleted on server`);
          }
          // Remove locally after server confirms
          await this.offline.deleteNote(note._id);
          await this.offline.deleteAttachmentsByNote(note._id);
          pushed++;
          this.pushFailures.delete(id);
          this.log.append('synch', `[PUSH] deleted ${id}`);
          continue;
        }

        // New note: use import endpoint (expects _id)
        if (note.syncStatus === 'new') {
          const { note: created, warnings } = await firstValueFrom(this.remote.importNoteFromOffline(note));
          // Preserve the local note's content; only adopt _rev from the server
          // response to avoid overwriting user edits with a potentially stale
          // server copy (e.g. when a 409 was resolved against an older revision).
          const toSave: Note = { ...note, ...(created?._rev ? { _rev: created._rev } : {}) };
          await this.offline.saveNote(toSave, true);
          pushed++;
          this.pushFailures.delete(id);
          this.log.append('synch', `[PUSH] created ${id}`);
          if (warnings.length) {
            warned++;
            this.log.append('synch', `[PUSH] ${id} warnings: ${warnings.join(' ')}`);
          }
          continue;
        }

        // Updated note: use updateNote (handles 409 and also saves offline as synced)
        if (note.syncStatus === 'updated') {
          await this.remote.updateNote(note);
          pushed++;
          this.pushFailures.delete(id);
          this.log.append('synch', `[PUSH] updated ${id}`);
          continue;
        }

        // Any other custom dirty state: attempt update as safest default
        await this.remote.updateNote(note);
        pushed++;
        this.pushFailures.delete(id);
        this.log.append('synch', `[PUSH] updated(default) ${id}`);
      } catch (e: any) {
        if (e instanceof NoteDeletedOnServerError) {
          // Edited here while it was moved to Trash elsewhere: the server's delete wins, and this
          // stale copy must not resurrect it. The note (server version) stays restorable from Trash.
          await this.offline.deleteNote(note._id);
          await this.offline.deleteAttachmentsByNote(note._id);
          this.pushFailures.delete(id);
          pushed++;
          this.log.append('synch', `[PUSH] ${id} was moved to Trash on the server — dropped the local copy (local edits not applied)`);
          continue;
        }
        failed++;
        const message = e?.message ?? String(e);
        this.recordPushFailure(id, message);
        this.log.append('synch', `[PUSH] failed for ${id}: ${message}`);
      }
    }

    this.log.append(
      'synch',
      `[PUSH] done: pushed=${pushed}, failed=${failed}` +
        ` — ${pushed} note(s) synced${warned ? `, ${warned} with obsolete/unknown fields ignored` : ''}${failed ? `, ${failed} failed` : ''}.`,
    );
    if (failed > 0) {
      this.errorReporter.report({
        message: `Push sync completed with ${failed} failure(s) (pushed=${pushed})`,
        error_type: 'SyncPushFailure',
        severity: 'warning',
        extra: { pushed, failed },
      });
    }

    return { pushed, failed };
  }

  /** Private tags queued for notes that weren't on the server yet (ScopedTagsService) — assigned once
   * the push has created them. Resolved lazily: ScopedTagsService depends on DataService. */
  private async flushPendingPrivateTags(): Promise<void> {
    if (!this.injector) return;
    try {
      const scopedTags = this.injector.get(ScopedTagsService);
      await scopedTags.flushPendingPrivateTags((noteId) => this.offline.getNote(noteId));
    } catch (err: any) {
      this.log.append('synch', `[PUSH] pending private tags not assigned yet: ${err?.message ?? err}`);
    }
  }

  /**
   * Applies a server-side delete (or move to Trash) of `id` to this device. Keeps the local copy when
   * it has unsynced edits, or when it already lives in another Space (the delete is the move's source
   * side — tasks/space-move-client.md).
   */
  private async applyServerDelete(
    id: string,
    local: Note | undefined,
    remoteSpaceId: string | undefined,
  ): Promise<'applied' | 'ignored' | 'skipped-dirty'> {
    if (!local) {
      this.log.append('synch', `...Ignoring delete for ${id}: not present locally`);
      return 'ignored';
    }
    if (local.syncStatus && local.syncStatus !== 'synced') {
      this.log.append('synch', `...Skipping delete ${id}: local changes present`);
      return 'skipped-dirty';
    }
    if (remoteSpaceId && local.spaceId && local.spaceId !== remoteSpaceId) {
      this.log.append('synch', `...Ignoring delete for ${id} from ${remoteSpaceId}: note now lives in ${local.spaceId}`);
      return 'ignored';
    }
    this.log.append('synch', `...Deleting note "${local.Title}" (${id}) as per server delete`);
    await this.offline.deleteNote(id);
    await this.offline.deleteAttachmentsByNote(id);
    return 'applied';
  }

  /** The note's current version via the normal notes API: 404 = no longer available to this user. */
  private async fetchCurrentNote(
    id: string,
    remoteSpaceId: string | undefined,
  ): Promise<{ kind: 'ok'; note: Note } | { kind: 'unavailable' } | { kind: 'error'; message: string }> {
    try {
      const note = await firstValueFrom(this.remote.getNote(id, remoteSpaceId));
      if (note?._id) return { kind: 'ok', note };
      return { kind: 'error', message: 'empty response' };
    } catch (err: any) {
      if (Number(err?.status) === 404) return { kind: 'unavailable' };
      return { kind: 'error', message: err?.message ?? String(err) };
    }
  }

  // ------------------------------------------------------------------
  // PUSH PENDING ATTACHMENT UPLOADS (queued while offline)
  // ------------------------------------------------------------------

  private async runPushAttachments(): Promise<{ pushed: number; failed: number }> {
    const pending = await this.offline.getPendingAttachmentUploads();
    if (pending.length === 0) {
      return { pushed: 0, failed: 0 };
    }

    this.log.append('synch', `[PUSH-ATT] ${pending.length} pending attachment upload(s)`);

    let pushed = 0;
    let failed = 0;

    for (const record of pending) {
      const { _id: tempId, noteId, filename, mimeType, blob } = record;

      if (!blob || !noteId) {
        this.log.append('synch', `[PUSH-ATT] skipping ${tempId}: missing blob or noteId`);
        failed++;
        continue;
      }

      try {
        // Fetched before the upload (not just for the Attachments-array patch below) so its
        // spaceId can be sent with the upload — otherwise the attachment would land in this
        // account's own PRIVATE Space's database regardless of which Space the note is in.
        const note = await this.offline.getNote(noteId);
        if (!note || note.localDraft) {
          // Like the note itself, its uploads wait for the first explicit save: the user may still
          // change the draft's Space, and the attachment must land in that Space's database.
          // (Not stored at all = a new note that was never autosaved.)
          this.log.append('synch', `[PUSH-ATT] deferring ${tempId}: note ${noteId} is not saved yet`);
          continue;
        }
        const file = new File([blob], filename, { type: mimeType || 'application/octet-stream' });
        const response = await firstValueFrom(this.fileUpload.uploadAttachment(file, noteId, note?.spaceId));

        if (response.status !== 'success' || !response.data?._id) {
          const msg = response.error?.message ?? 'No attachment ID returned';
          this.log.append('synch', `[PUSH-ATT] upload failed for ${tempId}: ${msg}`);
          failed++;
          continue;
        }

        const realId = response.data._id;

        // Replace tempId with realId in the note's Attachments array and in the inline image
        // references of its rich text (images dropped/inserted while queued).
        // The note will be re-saved as 'updated' so runPush() picks it up
        // and sends the corrected references to the server.
        if (note) {
          const attachments = Array.isArray(note.Attachments) ? note.Attachments : [];
          const updatedNote = {
            ...note,
            Attachments: attachments.map((id) => (id === tempId ? realId : id)),
            DescriptionHtml: replaceAttachmentIdInHtml(note.DescriptionHtml, tempId, realId),
            CommentsHtml: replaceAttachmentIdInHtml(note.CommentsHtml, tempId, realId),
          };
          const changed =
            attachments.includes(tempId) ||
            updatedNote.DescriptionHtml !== String(note.DescriptionHtml ?? '') ||
            updatedNote.CommentsHtml !== String(note.CommentsHtml ?? '');
          if (changed) {
            // syncMode=false marks note as 'updated' so runPush() will sync it
            await this.offline.saveNote(updatedNote, false);
            this.log.append('synch', `[PUSH-ATT] updated note ${noteId}: ${tempId} → ${realId}`);
          }
        }

        // Persist the blob under the real server ID and remove the temp record
        await this.offline.replaceAttachmentId(tempId, {
          _id: realId,
          noteId,
          filename,
          mimeType,
          size: blob.size,
          blob,
        });

        pushed++;
        this.log.append('synch', `[PUSH-ATT] done: ${filename} (${tempId} → ${realId})`);
      } catch (err: any) {
        failed++;
        this.log.append('synch', `[PUSH-ATT] error for ${tempId}: ${err?.message ?? String(err)}`);
      }
    }

    this.log.append('synch', `[PUSH-ATT] completed: pushed=${pushed}, failed=${failed}`);
    return { pushed, failed };
  }

  private async syncAttachmentsForNotes(notes: Note[], phase: '[BASELINE]' | '[PULL]'): Promise<void> {
    const noteList = Array.isArray(notes) ? notes : [];
    if (noteList.length === 0) {
      return;
    }

    for (const note of noteList) {
      const noteId = String(note?._id ?? '');
      if (!noteId.startsWith('note:') && !noteId.startsWith('template:')) {
        continue;
      }

      const attachmentIds = Array.isArray(note?.Attachments)
        ? note.Attachments.map((id) => String(id ?? '').trim()).filter((id) => !!id)
        : [];

      if (attachmentIds.length === 0) {
        // Prune (not delete-all) so a still-queued upload for this note survives the pull.
        await this.offline.pruneNoteAttachments(noteId, []);
        continue;
      }

      try {
        const metas = await firstValueFrom(this.fileUpload.getFiles(noteId, note?.spaceId));
        const idsToPrefetch = (Array.isArray(metas) ? metas : [])
          .map((item) => String(item?._id ?? '').trim())
          .filter((id) => !!id);
        const effectiveIds = idsToPrefetch.length > 0 ? idsToPrefetch : attachmentIds;

        for (const attachmentId of effectiveIds) {
          const meta = (metas || []).find((item) => String(item?._id ?? '') === attachmentId);
          await firstValueFrom(this.fileUpload.getFileForNote(noteId, attachmentId, meta?.filename, note?.spaceId));
        }
      } catch (err: any) {
        this.log.append(
          'synch',
          `${phase} attachment prefetch failed for ${noteId}: ${err?.message ?? String(err)}`,
        );
      }
    }
  }
}
